import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import WeeklyFeedback from '../components/feedback/WeeklyFeedback';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n/LanguageContext';
import './HelpCenterShell.css';
import '../styles/workspace-navigation.css';

export default function DashboardLayout() {
  const location = useLocation();
  const { user, sessionVersion } = useAuth();
  const resources = [
    '/resources',
    '/distributor/resources',
    '/admin/distributor-resources',
  ].includes(location.pathname);
  const help = ['/help-center', '/distributor/help'].includes(location.pathname) || resources;
  const [navigationOpen, setNavigationOpen] = useState(false);
  const [mobile, setMobile] = useState(
    () => window.matchMedia?.('(max-width: 767px)')?.matches || false
  );
  const navigationRef = useRef(null);
  const mainRef = useRef(null);
  const toggleRef = useRef(null);
  const drawerOpen = mobile && navigationOpen;
  const t = useT();
  useEffect(
    () => setNavigationOpen(false),
    [location.pathname, user?._id, user?.id, user?.role, user?.adminWorkspace, sessionVersion]
  );
  useEffect(() => {
    const media = window.matchMedia?.('(max-width: 767px)');
    if (!media) return undefined;
    const change = () => {
      setMobile(media.matches);
      setNavigationOpen(false);
    };
    media.addEventListener('change', change);
    return () => media.removeEventListener('change', change);
  }, []);
  useEffect(() => {
    if (navigationRef.current) navigationRef.current.inert = mobile && !drawerOpen;
  }, [mobile, drawerOpen]);
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const toggle = toggleRef.current;
    const main = mainRef.current;
    const previousInert = Boolean(main.inert);
    const previousAria = main.getAttribute('aria-hidden');
    const previousOverflow = document.body.style.overflow;
    main.inert = true;
    main.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = 'hidden';
    const focusable = () => [
      ...navigationRef.current.querySelectorAll('a[href],button:not([disabled])'),
    ];
    focusable()[0]?.focus();
    const keydown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setNavigationOpen(false);
      }
      if (event.key === 'Tab') {
        const items = focusable();
        if (!items.length) return;
        event.preventDefault();
        const index = items.indexOf(document.activeElement);
        items[(index + (event.shiftKey ? -1 : 1) + items.length) % items.length]?.focus();
      }
    };
    document.addEventListener('keydown', keydown);
    return () => {
      document.removeEventListener('keydown', keydown);
      document.body.style.overflow = previousOverflow;
      main.inert = previousInert;
      if (previousAria === null) main.removeAttribute('aria-hidden');
      else main.setAttribute('aria-hidden', previousAria);
      if (window.matchMedia?.('(max-width: 767px)')?.matches) toggle?.focus();
    };
  }, [drawerOpen]);
  return (
    <div
      className={`me-workspace-shell${help ? ' me-help-shell' : ''}${resources ? ' me-resource-shell' : ''}`}
    >
      <div className="me-workspace-mobile-bar">
        <button
          ref={toggleRef}
          type="button"
          aria-label={t(drawerOpen ? 'Close' : 'Navigation')}
          aria-expanded={drawerOpen}
          aria-controls="workspace-navigation"
          onClick={() => setNavigationOpen((value) => !value)}
        >
          <span className="material-symbols-outlined" aria-hidden="true">
            {drawerOpen ? 'close' : 'menu'}
          </span>
          {t(drawerOpen ? 'Close' : 'Navigation')}
        </button>
        <span>{resources ? t('Resources & Tools') : help ? t('Help Center') : 'MedEffects'}</span>
      </div>
      <div
        ref={navigationRef}
        id="workspace-navigation"
        className={`me-workspace-navigation${drawerOpen ? ' is-open' : ''}`}
        role={drawerOpen ? 'dialog' : undefined}
        aria-modal={drawerOpen ? 'true' : undefined}
        aria-label={drawerOpen ? t('Navigation') : undefined}
        aria-hidden={mobile && !drawerOpen ? 'true' : undefined}
        onClick={(event) => {
          if (event.target.closest('a[href]')) setNavigationOpen(false);
        }}
      >
        <button
          type="button"
          className="me-workspace-close"
          aria-label={t('Close')}
          onClick={() => setNavigationOpen(false)}
        >
          <span className="material-symbols-outlined" aria-hidden="true">
            close
          </span>
        </button>
        <Sidebar />
      </div>
      {drawerOpen && (
        <button
          type="button"
          className="me-workspace-backdrop"
          aria-label={t('Close')}
          onClick={() => setNavigationOpen(false)}
        />
      )}
      <main
        ref={mainRef}
        tabIndex={resources ? 0 : undefined}
        className={`me-workspace-main${help ? ' me-help-main' : ''}`}
      >
        <Outlet />
      </main>
      <WeeklyFeedback />
    </div>
  );
}
